import React, { forwardRef, useState } from 'react';
import { Box, Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import palette from 'theme/palette';
import CircleSector from 'components/common/CircleSector';
import TextTile from 'components/auth/TextTile';
import ImageTile from 'components/auth/ImageTile';
import CreateJliveAccountLayout from 'components/creatingOrder/CreateJliveAccount/CreateJliveAccountLayout';
import PaperComponent from 'components/modals/CreateJliveAccountModal/PaperComponent';
import TermsAndConditionsScreen from 'components/screens/TermsAndConditionsScreen';
import PrivacyPolicyScreen from 'components/screens/PrivacyPolicyScreen';

type DocumentType = 'terms' | 'privacy';

interface IProps {
  document: DocumentType;
  onClose: any;
  open: boolean;
}

const useStyles = makeStyles()((theme) => ({
  content: {
    ...theme.mixins.scrollBarOverflowYAuto,
    height: '100%',
    overflowY: 'auto',
  },
  // Unlike `CreateJliveAccountModal`'s white `CloseButton` (legible over its dark photo tiles),
  // this close button sits over the document's white content panel — so it needs its own
  // contrasting circular badge (dark icon on a white, shadowed disc) instead.
  closeBtn: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1,
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.16)',
    '&:hover': {
      backgroundColor: theme.palette.custom.gray.light,
    },
  },
}));

const DocumentModal: React.FC<IProps> = forwardRef((props) => {
  const { document, onClose, ...other } = props as IProps;
  const [t] = useTranslation('public');
  const { classes } = useStyles();
  const [currentDocument, setCurrentDocument] = useState<DocumentType>(document);

  const handleClose = () => onClose({}, null);

  return (
    <Dialog onClose={onClose} PaperComponent={PaperComponent} maxWidth="lg" {...other}>
      <CreateJliveAccountLayout
        images={
          <>
            <TextTile logo color="primary" />
            <ImageTile src="/static/img/annie-spratt-WS2anBsrum0.jpeg" />
            <ImageTile src="/static/img/tijs-van-leur-So6YckShOVA.jpeg">
              <CircleSector
                variant="top-right"
                position="absolute"
                backgroundColor={palette.custom.purple.main}
                radius={50}
                top={18}
                right={56}
              />
            </ImageTile>
            <TextTile
              text={t<string>('creatingOrder.orderSummaryContent.createJliveAccount.viewText')}
              color="secondary"
            />
          </>
        }
        content={
          <Box className={classes.content}>
            {currentDocument === 'terms' ? (
              <TermsAndConditionsScreen inModal onNavigate={setCurrentDocument} />
            ) : (
              <PrivacyPolicyScreen inModal onNavigate={setCurrentDocument} />
            )}
          </Box>
        }
      />
      <IconButton className={classes.closeBtn} onClick={handleClose} size="small">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Dialog>
  );
});

export default DocumentModal;
